import { useSyncExternalStore } from 'react';

export type ThemePreference = 'light' | 'dark' | 'auto';
type ThemeState = { preference: ThemePreference; resolved: 'light' | 'dark' };

const STORAGE_KEY = 'campaigns.theme';
const listeners = new Set<() => void>();
let snapshot: ThemeState | undefined;
let mediaQuery: MediaQueryList | undefined;

function parsePreference(value: string | null): ThemePreference {
  return value === 'light' || value === 'dark' ? value : 'auto';
}

function readStored(): ThemePreference {
  try {
    return parsePreference(localStorage.getItem(STORAGE_KEY));
  } catch {
    return 'auto';
  }
}

function resolve(preference: ThemePreference): ThemeState['resolved'] {
  if (preference !== 'auto') return preference;

  return (mediaQuery ?? window.matchMedia('(prefers-color-scheme: dark)')).matches
    ? 'dark'
    : 'light';
}

function getSnapshot(): ThemeState {
  if (snapshot === undefined) {
    const preference = readStored();
    snapshot = { preference, resolved: resolve(preference) };
  }

  return snapshot;
}

function apply(preference: ThemePreference): void {
  const resolved = resolve(preference);
  const previous = snapshot;

  // These colours mirror --bg and --surface; index.html applies them before React boots.
  document.documentElement.classList.toggle('dark', resolved === 'dark');
  document.documentElement.style.colorScheme = resolved;
  document.documentElement.style.backgroundColor = resolved === 'dark' ? '#0a1c30' : '#f4f6f8';
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', resolved === 'dark' ? '#102941' : '#ffffff');

  if (previous?.preference === preference && previous.resolved === resolved) return;

  snapshot = { preference, resolved };
  for (const listener of listeners) listener();
}

function onSystemChange(): void {
  if (getSnapshot().preference === 'auto') apply('auto');
}

function onStorage(event: StorageEvent): void {
  if (event.key === STORAGE_KEY || event.key === null) {
    apply(parsePreference(event.newValue));
  }
}

function subscribe(listener: () => void): () => void {
  if (listeners.size === 0) {
    mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', onSystemChange);
    window.addEventListener('storage', onStorage);
    apply(readStored());
  }
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
    if (listeners.size !== 0) return;

    mediaQuery?.removeEventListener('change', onSystemChange);
    window.removeEventListener('storage', onStorage);
    mediaQuery = undefined;
    snapshot = undefined;
  };
}

function setPreference(next: ThemePreference): void {
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // A blocked store must not prevent the choice from applying for this session.
  }
  apply(next);
}

function toggle(): void {
  setPreference(getSnapshot().resolved === 'dark' ? 'light' : 'dark');
}

/**
 * One store for every consumer, matching ServiceDesk's shared theme state. A toolbar choice also
 * changes Monaco and the phone menu; one OS listener cannot overwrite another consumer's choice.
 * index.html owns the first paint, and this store keeps the document and browser chrome in sync.
 */
export function useTheme(): ThemeState & {
  setPreference: (next: ThemePreference) => void;
  toggle: () => void;
} {
  const state = useSyncExternalStore(subscribe, getSnapshot);
  return { ...state, setPreference, toggle };
}
